import React from 'react';
import {
  ArrayInput,
  SimpleFormIterator,
  useTranslate,
} from 'react-admin';
import { useWatch } from 'react-hook-form';
import { Box } from '@mui/material';
import { ScopedIteratorFields } from '../event-utils/ScopedIteratorFields';
import {
  StandardProductForm as SharedStandardProductForm,
  SeatedProductForm as SharedSeatedProductForm,
} from '../../stocks/StocksProductForm';
import { normalizeEventType } from '../../../utils/eventFormUtils';
import { useSourceLanguage } from '../../../hooks/useSourceLanguage';
import { SourceLanguageBanner } from '../SourceLanguageBanner';

/**
 * Product Form Manager Component
 * Manages product/ticket forms based on event type
 */
export const ProductFormManager = ({ eventType, disabled = false, isEdit = false, record = {} }) => {
  const formType = useWatch({ name: 'type' });
  const resolvedType = normalizeEventType(formType ?? eventType);
  const { sourceLang } = useSourceLanguage({ isEdit, record });

  if (resolvedType === 1 || resolvedType === 2) {
    return <StandardProductForm disabled={disabled} sourceLang={sourceLang} record={record} isEdit={isEdit} />;
  }
  if (resolvedType === 3) {
    return <SeatedProductForm disabled={disabled} sourceLang={sourceLang} record={record} isEdit={isEdit} />;
  }
  // Prefer prop if watch briefly returns empty while remounting
  if (eventType === 1 || eventType === 2) {
    return <StandardProductForm disabled={disabled} sourceLang={sourceLang} record={record} isEdit={isEdit} />;
  }
  if (eventType === 3) {
    return <SeatedProductForm disabled={disabled} sourceLang={sourceLang} record={record} isEdit={isEdit} />;
  }
  return null;
};

/**
 * Standard Product Form Component
 * For Standard and Area Layout events
 */
const StandardProductForm = ({ disabled = false, sourceLang, record, isEdit }) => {
  const translate = useTranslate();
  return (
    <React.Fragment>
      <SourceLanguageBanner isEdit={isEdit} record={record} />
      <Box display={{ xs: 'block', sm: 'flex', width: '100%', marginBottom: 20 }}>
        <Box flex={1} mr={{ xs: 0, sm: '0.5em' }}>
          <ProductFormSection title={translate('form.special_deal', 'Special Deal')} source="tickets.deal" disabled={disabled} sourceLang={sourceLang} />
        </Box>
        <Box flex={1} ml={{ xs: 0, sm: '0.5em' }}>
          <ProductFormSection title={translate('form.reservation', 'Reservation')} source="tickets.guestlist" disabled={disabled} sourceLang={sourceLang} />
        </Box>
      </Box>

      <Box display={{ xs: 'block', sm: 'flex', width: '100%', marginBottom: 20 }}>
        <Box flex={1} mr={{ xs: 0, sm: '0.5em' }}>
          <ProductFormSection title={translate('form.general_ticket', 'General')} source="tickets.general" disabled={disabled} sourceLang={sourceLang} />
        </Box>
        <Box flex={1} ml={{ xs: 0, sm: '0.5em' }}>
          <ProductFormSection title={translate('form.vip_list', 'VIP List')} source="tickets.vip" disabled={disabled} sourceLang={sourceLang} />
        </Box>
      </Box>

      <Box display={{ xs: 'block', sm: 'flex', width: '100%' }}>
        <Box flex={1} mr={{ xs: 0, sm: '0.5em' }}>
          <ProductFormSection title={translate('form.extras', 'Extras')} source="tickets.extras" disabled={disabled} sourceLang={sourceLang} />
        </Box>
        <Box flex={1} ml={{ xs: 0, sm: '0.5em' }}>
          <ProductFormSection
            title={translate('form.invitations', 'Invitations')}
            source="tickets.invitation"
            disabled={disabled}
            sourceLang={sourceLang}
            helperText={translate('form.invitations_hint', 'Can create invitations for guests to attend the event.')}
          />
        </Box>
      </Box>
    </React.Fragment>
  );
};

/**
 * Seated Product Form Component
 * For Seated events
 */
const SeatedProductForm = ({ disabled = false, sourceLang, record, isEdit }) => {
  const translate = useTranslate();
  return (
    <React.Fragment>
      <SourceLanguageBanner isEdit={isEdit} record={record} />
      <ArrayInput source="tickets" label={translate('form.tickets', 'Tickets')} defaultValue={[]}>
      <SimpleFormIterator
        disableReordering
        className="sessionEventTicketsArray"
        disableRemove={disabled}
        disableAdd={disabled}
      >
        <ScopedIteratorFields>
          {({ formData, scopedFormData, getSource, ...rest }) => (
            <SeatedProductFormFields
              getSource={getSource}
              scopedFormData={scopedFormData}
              disabled={disabled}
              sourceLang={sourceLang}
            />
          )}
        </ScopedIteratorFields>
      </SimpleFormIterator>
    </ArrayInput>
    </React.Fragment>
  );
};

/**
 * Product Form Section Component
 * Individual product form section with title and form fields
 */
const ProductFormSection = ({ title, source, disabled = false, helperText = null, sourceLang }) => {
  const translate = useTranslate();
  return (
    <div style={{ border: '1px dashed #000', padding: 5 }}>
      <h3>
        {title}{' '}
        <span style={{ color: 'orange', fontStyle: 'italic', fontSize: 12 }}>
          {translate('form.all_fields_required', '(* all fields required)')}
        </span>
      </h3>
      {helperText && (
        <span
          style={{
            backgroundColor: '#0ABAB5',
            color: 'white',
            borderRadius: 10,
            fontSize: 12.5,
            padding: 5,
            marginTop: -10,
            marginBottom: 8,
            display: 'inline-block',
          }}
        >
          {helperText}
        </span>
      )}
      <ArrayInput source={source} label={title} defaultValue={[]}>
        <SimpleFormIterator disableRemove={disabled} disableAdd={disabled}>
          <ScopedIteratorFields>
            {({ formData, scopedFormData, getSource, ...rest }) => (
              <StandardProductFormFields
                getSource={getSource}
                scopedFormData={scopedFormData}
                disabled={disabled}
                sourceLang={sourceLang}
              />
            )}
          </ScopedIteratorFields>
        </SimpleFormIterator>
      </ArrayInput>
    </div>
  );
};

const StandardProductFormFields = ({ getSource, scopedFormData, disabled = false, sourceLang }) => {
  return (
    <SharedStandardProductForm
      getSource={getSource}
      showSold={false}
      scopedFormData={scopedFormData || {}}
      isEditForm={false}
      disabled={disabled}
      sourceLang={sourceLang}
    />
  );
};

const SeatedProductFormFields = ({ getSource, scopedFormData, disabled = false, sourceLang }) => {
  return (
    <SharedSeatedProductForm
      getSource={getSource}
      showSold={false}
      scopedFormData={scopedFormData || {}}
      isEditForm={false}
      disabled={disabled}
      sourceLang={sourceLang}
    />
  );
};
